import { Injectable } from '@angular/core';
import { UserService } from '../../core/services/user.service';
import { BehaviorSubject, combineLatest, fromEvent, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, pairwise, startWith, takeUntil } from 'rxjs/operators';
import { User } from '../../core/models/user.model';
import { CommonUtilService } from './CommonUtil.service';
import { FeatureFlagService } from './feature-flag.service';
import { FeatureFlagsEnum } from '../enums';
import { AuthenticationService } from '../../core/services/authentication.service';

/**
 * Helper functions for set/get/delete cookies copied from datadog package
 */

export interface CookieOptions {
    secure?: boolean;
    crossSite?: boolean;
    domain?: string;
}

export function findCommaSeparatedValue(rawString: string, name: string) {
    const regex = new RegExp(`(?:^|;)\\s*${name}\\s*=\\s*([^;]+)`)
    const matches = regex.exec(rawString)
    return matches ? matches[1] : undefined
}

export function setCookie(name: string, value: string, expireDelay: number, options?: CookieOptions) {
    const date = new Date()
    date.setTime(date.getTime() + expireDelay)
    const expires = `expires=${date.toUTCString()}`
    const sameSite = options && options.crossSite ? 'none' : 'strict'
    const domain = options && options.domain ? `;domain=${options.domain}` : ''
    const secure = options && options.secure ? ';secure' : ''
    document.cookie = `${name}=${value};${expires};path=/;samesite=${sameSite}${domain}${secure}`
}

export function getCookie(name: string) {
    return findCommaSeparatedValue(document.cookie, name)
}

export function deleteCookie(name: string, options?: CookieOptions) {
    setCookie(name, '', 0, options)
}

@Injectable({
    providedIn: 'root'
})
export class SessionCookieService {
    private readonly sessionCookieName: string = 'si_salesintel_session';
    private readonly sessionDelay: number = 1000 * 60 * 30; // 30 min
    private readonly churnZeroEventMessage: string = 'Portal - New Session';
    private readonly cookieSubject: BehaviorSubject<string> = new BehaviorSubject<string>(getCookie(this.sessionCookieName));
    private readonly userEvents$: Observable<MouseEvent[]> = combineLatest([
        fromEvent<MouseEvent>(document, 'click').pipe(startWith(null)),
        fromEvent<MouseEvent>(document, 'keyup').pipe(startWith(null)),
    ]).pipe(debounceTime(1000));

    constructor(
        private readonly userService: UserService,
        private readonly commonUtilService: CommonUtilService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly authenticationService: AuthenticationService,
    ) {}

    init(): void {
        this.listenCookies();
        this.listenCookiesAndLogout();
        this.setCookies();
    }

    private setCookies(): void {
        combineLatest([
            this.userService.getUserData().pipe(pairwise()),
            this.userEvents$,
        ])
        .pipe(
            debounceTime(500),
            takeUntil(this.authenticationService.loggedOut$),
        )
        .subscribe(([[oldData, newData]]: [User[], MouseEvent[]]) => {
            const cookie: string = getCookie(this.sessionCookieName);

            if (newData) {
                if (!cookie) {
                    this.commonUtilService.churnZeroEvents(this.churnZeroEventMessage);
                }

                setCookie(this.sessionCookieName, this.sessionCookieName, this.sessionDelay);
            }

            if (oldData && !newData && cookie) {
                deleteCookie(this.sessionCookieName);
            }
        });
    }

    private listenCookies(): void {
        setInterval(() => {
            const cookie: string = getCookie(this.sessionCookieName);
            this.cookieSubject.next(cookie);
        }, 500);
    }

    private listenCookiesAndLogout(): void {
        combineLatest([
            this.cookieSubject.asObservable().pipe(distinctUntilChanged(), pairwise()),
            this.featureFlagService.getFlag(FeatureFlagsEnum.SessionLogout),
        ]).pipe(takeUntil(this.authenticationService.loggedOut$))
        .subscribe(([[oldCookies, newCookies], flag]: [string[], boolean]) => {
            if (flag && oldCookies && !newCookies) {
                this.authenticationService.navigateToLogin();
            }
        });
    }
}
